import { useEffect, useState } from "react";
import { api, BRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, Wallet, CalendarClock, ArrowRightLeft, SlidersHorizontal } from "lucide-react";
import { RECUR_LABELS } from "@/components/ExpenseDialog";

export default function Forecast() {
  const [months, setMonths] = useState("6");
  const [customMonths, setCustomMonths] = useState("");
  const [status, setStatus] = useState("all");
  const [recurringOnly, setRecurringOnly] = useState(false);
  const [start, setStart] = useState("");
  const [data, setData] = useState(null);

  const effectiveMonths = customMonths ? Math.max(1, Math.min(36, parseInt(customMonths) || 6)) : parseInt(months);

  useEffect(() => {
    const params = new URLSearchParams({ months: String(effectiveMonths), status, recurring_only: String(recurringOnly) });
    if (start) params.set("start", start);
    api.get(`/forecast?${params.toString()}`).then((r) => setData(r.data));
  }, [effectiveMonths, status, recurringOnly, start]);

  const stats = [
    { label: "Total provisionado", value: BRL(data?.horizon_total || 0), icon: Wallet, tone: "text-primary", testid: "forecast-total-card" },
    { label: "Média mensal", value: BRL(data?.avg_monthly || 0), icon: TrendingUp, tone: "", testid: "forecast-avg-card" },
    { label: "Maior mês", value: BRL(data?.peak_month || 0), icon: ArrowRightLeft, tone: "text-amber-600 dark:text-amber-500", testid: "forecast-peak-card" },
    { label: "Lançamentos previstos", value: data?.count ?? 0, icon: CalendarClock, tone: "", testid: "forecast-count-card" },
  ];

  return (
    <div className="fade-up space-y-8">
      <div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">Previsões</h1>
        <p className="text-muted-foreground mt-1">Provisionamento flexível e visão futura das despesas</p>
      </div>

      <Card className="p-5 border-border/60 shadow-none rounded-md" data-testid="forecast-controls">
        <div className="flex items-center gap-2 mb-4 text-sm font-semibold text-muted-foreground">
          <SlidersHorizontal className="h-4 w-4" aria-hidden /> Configurar previsão
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-end">
          <div className="space-y-2">
            <Label>Período</Label>
            <Tabs value={customMonths ? "custom" : months} onValueChange={(v) => { if (v !== "custom") { setMonths(v); setCustomMonths(""); } }}>
              <TabsList data-testid="forecast-range-tabs" className="w-full">
                <TabsTrigger value="2" data-testid="range-2">2m</TabsTrigger>
                <TabsTrigger value="3" data-testid="range-3">3m</TabsTrigger>
                <TabsTrigger value="4" data-testid="range-4">4m</TabsTrigger>
                <TabsTrigger value="5" data-testid="range-5">5m</TabsTrigger>
                <TabsTrigger value="6" data-testid="range-6">6m</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="space-y-2">
            <Label>Meses personalizado (1-36)</Label>
            <Input type="number" min="1" max="36" placeholder="ex: 18" data-testid="forecast-custom-months" value={customMonths} onChange={(e) => setCustomMonths(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>A partir de</Label>
            <Input type="date" data-testid="forecast-start-date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="space-y-3">
            <Label>Filtros</Label>
            <div className="flex items-center gap-4">
              <Tabs value={status} onValueChange={setStatus}>
                <TabsList data-testid="forecast-status-tabs">
                  <TabsTrigger value="all" data-testid="status-all">Todas</TabsTrigger>
                  <TabsTrigger value="pending" data-testid="status-pending">A pagar</TabsTrigger>
                </TabsList>
              </Tabs>
              <label className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap">
                <Switch checked={recurringOnly} onCheckedChange={setRecurringOnly} data-testid="forecast-recurring-switch" />
                Só recorrentes
              </label>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((s) => (
          <Card key={s.label} className="p-6 border-border/60 shadow-none rounded-md" data-testid={s.testid}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground font-medium">{s.label}</span>
              <s.icon className={`h-4 w-4 ${s.tone || "text-muted-foreground"}`} aria-hidden />
            </div>
            <p className={`font-head text-3xl font-extrabold tracking-tight tnum ${s.tone}`}>{s.value}</p>
          </Card>
        ))}
      </div>

      <Card className="p-6 border-border/60 shadow-none rounded-md" data-testid="forecast-bar-chart">
        <h2 className="font-head text-lg font-bold tracking-tight mb-6">Despesas provisionadas por mês</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.months || []} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 88%)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => BRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(150 8% 88%)", fontSize: 13 }} cursor={{ fill: "hsl(214 95% 93%)" }} />
              <Bar dataKey="total" fill="hsl(221 83% 53%)" radius={[6, 6, 0, 0]} name="Despesas" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6 border-border/60 shadow-none rounded-md" data-testid="forecast-cumulative-chart">
        <h2 className="font-head text-lg font-bold tracking-tight mb-1">Acumulado projetado</h2>
        <p className="text-sm text-muted-foreground mb-6">Linha tracejada representa valores futuros estimados</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.months || []} margin={{ left: 0, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 88%)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => BRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(214 20% 88%)", fontSize: 13 }} />
              <Line type="monotone" dataKey="cumulative" stroke="hsl(221 83% 53%)" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3 }} name="Acumulado" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="border-border/60 shadow-none rounded-md overflow-hidden" data-testid="forecast-detail-table">
        <div className="p-6 pb-3">
          <h2 className="font-head text-lg font-bold tracking-tight">Lançamentos previstos</h2>
          <p className="text-sm text-muted-foreground">Cada ocorrência projetada no período selecionado</p>
        </div>
        {(!data || data.occurrences.length === 0) ? (
          <div className="py-12 text-center text-muted-foreground">Nenhum lançamento previsto para os filtros atuais.</div>
        ) : (
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Despesa</TableHead>
                  <TableHead>Recorrência</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.occurrences.map((o, i) => (
                  <TableRow key={i} data-testid={`forecast-occurrence-${i}`}>
                    <TableCell className="tnum">{new Date(o.date).toLocaleDateString("pt-BR")}</TableCell>
                    <TableCell className="font-medium">{o.name}</TableCell>
                    <TableCell><Badge variant="secondary" className="font-normal">{RECUR_LABELS[o.recurrence]}</Badge></TableCell>
                    <TableCell className="text-right font-semibold tnum">{BRL(o.value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
